import { client } from "@/shared/api/pref-storage";
import { FilterNotifications } from "@/types/notifications";

// Types matching the OpenAPI spec
export interface TagsWatchlist {
  id: number;
  name: string;
  tags: string[];
  notifications?: FilterNotifications;
  owner_type: "USER" | "NEWSQUAWK" | "EXTERNAL";
  /**
   * Desk-picked asset classes (`DESK_CATEGORY_OPTIONS`). Multiple values are
   * normal — a Fed list is macro and fixed_income — and `["all"]` marks a
   * cross-asset list. Empty means nobody has classified it yet.
   */
  categories?: string[];
  /**
   * Desk-authored, customer-facing: one line on what this list covers.
   * Set on NEWSQUAWK-owned records only. Max 500 server-side.
   */
  description?: string | null;
  /**
   * Desk-authored, INTERNAL: semicolon-separated phrases a trader might say,
   * feeding concierge retrieval. Never render this to a customer — it is
   * trigger text, not copy. Max 2000 server-side.
   */
  when_to_use?: string | null;
}

export interface TagsWatchlistCreate {
  name: string;
  tags: string[];
  notifications?: FilterNotifications;
  /** "NEWSQUAWK" requires the desk role — the server 403s otherwise. */
  owner_type?: "USER" | "NEWSQUAWK" | "EXTERNAL";
  categories?: string[];
  description?: string | null;
  when_to_use?: string | null;
}

export interface TagsWatchlistUpdate {
  name?: string;
  tags?: string[];
  /** When sent, replaces the whole backend-source map — always send the complete map. */
  notifications?: FilterNotifications;
  // owner_type is deliberately absent: the server ignores it on PATCH, so a
  // record cannot move into or out of the Newsquawk library after creation.
  categories?: string[];
  description?: string | null;
  when_to_use?: string | null;
}

const endpoints = {
  tagsWatchlists: "/tags-watchlists",
  tagsWatchlist: (id: number) => `/tags-watchlists/${id}`,
};

export const getTagsWatchlists = (offset: number = 0, limit: number = 100) => {
  return client.get<TagsWatchlist[]>(endpoints.tagsWatchlists, {
    params: { offset, limit },
  });
};

export const createTagsWatchlist = (data: TagsWatchlistCreate) => {
  return client.post<TagsWatchlist>(endpoints.tagsWatchlists, data);
};

export const getTagsWatchlist = (id: number) => {
  return client.get<TagsWatchlist>(endpoints.tagsWatchlist(id));
};

export const updateTagsWatchlist = (id: number, data: TagsWatchlistUpdate) => {
  return client.patch<TagsWatchlist>(endpoints.tagsWatchlist(id), data);
};

export const deleteTagsWatchlist = (id: number) => {
  return client.delete(endpoints.tagsWatchlist(id));
};
